<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <!-- Top: brief + count + buttons -->
    <div class="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3 space-y-3">
      <div class="flex flex-col gap-3">
        <textarea
          v-model="store.planBrief.value"
          rows="3"
          class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-none"
          placeholder="Describe your content plan — themes, goals, angles..."
        ></textarea>
        <div class="flex items-center gap-2">
          <div class="flex items-center gap-1.5">
            <label class="text-xs text-gray-500">Ideas</label>
            <input
              v-model.number="store.planCount.value"
              type="number"
              min="1"
              max="30"
              class="w-14 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-teal-500"
            />
          </div>
          <button
            v-if="!store.planGenerating.value"
            @click="store.generatePlan()"
            :disabled="!canGenerate"
            class="flex-1 px-3 py-1.5 rounded text-sm font-medium text-white transition-colors"
            :class="canGenerate ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-300 cursor-not-allowed'"
          >Generate</button>
          <button
            v-else
            @click="store.stopPlanGeneration()"
            class="flex-1 px-3 py-1.5 rounded text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors"
          >Stop</button>
        </div>
      </div>

      <p v-if="knowledgeCount > 0" class="text-xs text-gray-500">
        {{ knowledgeCount }} knowledge item{{ knowledgeCount > 1 ? 's' : '' }} will be included as context.
      </p>
    </div>

    <!-- Main content area -->
    <div class="flex-1 overflow-y-auto">
      <!-- Idle: no generation yet -->
      <div v-if="mode === 'idle'" class="flex-1 flex items-center justify-center text-gray-500 text-sm italic p-6">
        Enter a brief and click Generate.
      </div>

      <!-- Generating: streaming text -->
      <div v-else-if="mode === 'generating'" class="p-4">
        <div class="flex items-center gap-2 mb-3">
          <div class="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <span class="text-sm text-gray-600">Generating ideas...</span>
        </div>
        <pre class="text-sm text-gray-900 whitespace-pre-wrap font-sans leading-relaxed">{{ store.planStreamingText.value }}</pre>
      </div>

      <!-- Done: parsed entries -->
      <div v-else-if="mode === 'done'" class="p-4">
        <div v-if="store.planGenerationError.value" class="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 mb-3">
          {{ store.planGenerationError.value }}
        </div>

        <p class="text-sm text-gray-600 mb-2">{{ store.planParsedEntries.value.length }} ideas generated.</p>

        <div class="divide-y divide-gray-200 border-t border-gray-200">
          <div
            v-for="(entry, index) in store.planParsedEntries.value"
            :key="index"
            class="flex items-start gap-2 py-2"
          >
            <span class="text-xs font-medium text-gray-400 mt-0.5 shrink-0">{{ index + 1 }}.</span>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900">{{ entry.title }}</p>
              <p class="text-sm text-gray-600">{{ entry.description }}</p>
            </div>
            <button
              @click="store.removePlanEntry(index)"
              class="text-gray-400 hover:text-red-500 transition-colors shrink-0 mt-0.5"
              title="Remove"
            >&times;</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Action bar when done -->
    <div v-if="mode === 'done' && store.planParsedEntries.value.length > 0" class="shrink-0 border-t border-gray-200 px-4 py-3 bg-white space-y-2">
      <!-- With publication selected: append -->
      <template v-if="store.selectedPublication.value">
        <p class="text-sm text-gray-600">
          Append {{ store.planParsedEntries.value.length }} entries to
          <strong>{{ store.selectedPublication.value.name }}</strong>
        </p>
        <div class="flex gap-2">
          <button
            @click="handleAppend"
            :disabled="appending"
            class="flex-1 px-3 py-1.5 rounded text-sm font-medium text-white transition-colors"
            :class="!appending ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-300 cursor-not-allowed'"
          >{{ appending ? 'Appending...' : 'Append' }}</button>
          <button
            @click="store.resetPlanGeneration()"
            class="px-3 py-1.5 rounded text-sm text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
          >Discard</button>
        </div>
      </template>

      <!-- No publication: save as new -->
      <template v-else>
        <input
          v-model="publicationName"
          type="text"
          placeholder="Plan name..."
          class="w-full text-sm border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-teal-500"
        />
        <div class="flex gap-2">
          <button
            @click="handleSave"
            :disabled="store.planSaving.value || !publicationName.trim()"
            class="flex-1 px-3 py-1.5 rounded text-sm font-medium text-white transition-colors"
            :class="publicationName.trim() && !store.planSaving.value ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-300 cursor-not-allowed'"
          >{{ store.planSaving.value ? 'Saving...' : 'Save as new plan' }}</button>
          <button
            @click="store.resetPlanGeneration()"
            class="px-3 py-1.5 rounded text-sm text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
          >Discard</button>
        </div>
      </template>

      <p v-if="store.planSaveError.value" class="text-sm text-red-700">{{ store.planSaveError.value }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue'

const store = inject('workbench')
const publicationName = ref('')
const appending = ref(false)

const knowledgeCount = computed(() =>
  store.currentProject.value?.knowledge_items?.length || 0
)

const canGenerate = computed(() =>
  store.currentProject.value &&
  store.planBrief.value.trim() &&
  !store.planGenerating.value
)

const mode = computed(() => {
  if (store.planGenerating.value || (!store.planGenerationDone.value && store.planStreamingText.value)) {
    return 'generating'
  }
  if (store.planGenerationDone.value && store.planParsedEntries.value.length > 0) {
    return 'done'
  }
  return 'idle'
})

async function handleSave() {
  await store.savePlan(publicationName.value)
  publicationName.value = ''
}

async function handleAppend() {
  appending.value = true
  try {
    await store.appendEntriesToPublication(
      store.selectedPublication.value.id,
      store.planParsedEntries.value
    )
  } finally {
    appending.value = false
  }
}
</script>
